/**
 * Content Sub-Tabs Component (AG-006 / AG-008).
 * Renders contextual Layer-3 sub-navigation chips INSIDE the page content area,
 * below the page header, eliminating stacked pill rows from global chrome.
 */
import { Link, useRouterState } from "@tanstack/react-router";
import { isFirmPrimaryPath } from "@/lib/firm-primary-nav";
import { getActiveWorkspace } from "@/lib/workspace-registry";
import { useContentSubtabsKeyboard } from "@/hooks/use-content-subtabs-keyboard";
import { cn } from "@/lib/utils";

export function ContentSubTabs() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const search = useRouterState({ select: (s) => s.location.search });

  const workspace = isFirmPrimaryPath(pathname) ? getActiveWorkspace(pathname) : null;
  const items = workspace?.items ?? [];
  useContentSubtabsKeyboard(items, Boolean(workspace?.items?.length));

  if (!isFirmPrimaryPath(pathname)) return null;
  if (!workspace?.items?.length) return null;

  return (
    <nav
      className="sticky top-0 z-10 -mx-3 sm:-mx-4 md:-mx-6 px-3 sm:px-4 md:px-6 flex items-center gap-1.5 overflow-x-auto scrollbar-none py-1.5 mb-3 border-b border-border/40 bg-background/95 backdrop-blur-md shrink-0 no-print"
      aria-label={`${workspace.title} sub-navigation`}
      data-testid="content-sub-tabs"
    >
      {workspace.items.map((item, index) => {
        const isActive =
          pathname === item.to ||
          (item.to !== workspace.baseRoute && pathname.startsWith(item.to));
        const contextBadge = index < 9 ? String(index + 1) : item.badge;

        return (
          <Link
            key={item.id}
            to={item.to as never}
            params={(item.params ?? {}) as never}
            search={(item.search ?? search) as never}
            data-subtab-chip="true"
            tabIndex={isActive ? 0 : -1}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium whitespace-nowrap transition-all border shrink-0 cursor-pointer",
              isActive
                ? "bg-primary text-primary-foreground border-primary font-semibold shadow-xs"
                : "bg-background/80 text-muted-foreground hover:text-foreground border-border hover:border-gold/40 hover:bg-gold/5",
            )}
            title={item.description}
            aria-current={isActive ? "page" : undefined}
          >
            <span>{item.title}</span>
            {contextBadge ? (
              <span className="rounded bg-muted px-1.5 py-0.2 text-[9px] font-mono text-muted-foreground" title="Alt+number">
                {contextBadge}
              </span>
            ) : null}
          </Link>
        );
      })}
    </nav>
  );
}
